import { Fragment, ReactNode, useMemo, useState } from 'react';
import { generatePath, useParams } from 'react-router-dom';

import { AnimatePresence } from 'framer-motion';
import styled from 'styled-components';

import { Slide } from '@components/animators/slide';
import { Badge } from '@components/badge';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { DesignAvatar } from '@components/design-avatar';
import { EmptySearchState } from '@components/empty-search-state';
import { FocusBox } from '@components/focus-box';
import { IconButton } from '@components/icon-button';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconAttributeJourney } from '@components/icons/attribute-journey';
import { IconCloseRegular } from '@components/icons/close-regular';
import { IconDirectionRight } from '@components/icons/direction-right';
import { IconDocument } from '@components/icons/document';
import { IconForm } from '@components/icons/form';
import { IconOpenNewTab } from '@components/icons/open-new-tab';
import { IconReturn } from '@components/icons/return';
import { IconSidemenuRightOpen } from '@components/icons/sidemenu-right-open';
import { IconStatus } from '@components/icons/status';
import { IconSubIndicator } from '@components/icons/sub-indicator';
import { IconVisibilityShown } from '@components/icons/visibility-shown';
import { InlineSidebar } from '@components/inline-sidebar/inline-sidebar';
import { SearchBox } from '@components/search-box';
import { SquareImage } from '@components/square-image';
import { Horizontal, Stack } from '@components/stack';
import { Switch } from '@components/switch';
import { SmallColumnIcon } from '@components/table/column-icon';
import { ColumnManager } from '@components/table/fields-sidebar';
import { TableStore } from '@components/table/table-store';
import { AvailableColumn, ColumnType } from '@components/table/types';
import { Text } from '@components/text';
import { TitleDescription } from '@components/title-description';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

import {
  ACTION_TYPE,
  ActionsDataAction,
  ActionsDataField,
  TravellerJourneyListItem,
} from 'api/types';
import { ActionIcon } from 'ui/actions/action-icon';
import { ActionLabel, actionLabels } from 'ui/actions/action-label';
import { routePaths } from 'ui/authenticated/navigation';
import { nodeTypes } from 'ui/journey-builder/node-types';

import { makeColumnId, makeVariableColumnId } from './traveller-journey-list/columns';
import { excludedActionTypes } from './traveller-journey-list/constants';
import { ExtraStateStore } from './traveller-journey-list/extra-state-store';
import { useTravellerJourneyListTranslate } from './traveller-journey-list/locale';
import { TravellersColumnControlStore } from './traveller-journey-list/travellers-column-control-store';

export const CustomColumnControl = ({
  actionsData,
  availableColumns,
  tableStore,
  extraStateStore,
  columnControlStore,
}: {
  actionsData: Array<ActionsDataAction>;
  tableStore: TableStore;
  availableColumns: Array<AvailableColumn<TravellerJourneyListItem>>;
  extraStateStore: ExtraStateStore;
  columnControlStore: TravellersColumnControlStore;
}) => {
  const { t } = useScopedTranslate(locale);
  const { useColumnOrder, useColumnVisibility } = tableStore;
  const { columnVisibility, setColumnVisibility } = useColumnVisibility();
  const { columnOrder, setColumnOrder } = useColumnOrder();
  const { actions, useFocusedVariableId, useIsOpen, useSelectedActionId } = columnControlStore;

  const availableIds = useMemo(
    () => availableColumns.map((column) => column.id),
    [availableColumns]
  );

  const focusedVariableId = useFocusedVariableId();
  const isOpen = useIsOpen();
  const selectedActionId = useSelectedActionId();

  const allowedActions = useMemo(() => {
    return actionsData.filter((action) => {
      return !excludedActionTypes.includes(action.action);
    });
  }, [actionsData]);

  const selectedAction = selectedActionId
    ? allowedActions.find((action) => action.shortId === selectedActionId)
    : null;

  const columnsWithCustomNamesAndClickHandlers = useMemo(() => {
    return availableColumns.map((column) => {
      return {
        ...column,
        title: column.title,
        ...(column.id.startsWith('action__') && {
          extraContent: (
            <IconButton
              size="sm"
              variant="ghost-purple"
              icon={<IconSidemenuRightOpen size={16} />}
              onClick={() => {
                actions.focusColumn(column.id);
              }}
            />
          ),
        }),
      };
    });
  }, [availableColumns, actions]);

  return (
    <>
      <Button
        size="sm"
        variant="ghost"
        iconLeft={<IconVisibilityShown />}
        text={t('columns')}
        onClick={() => actions.setOpenness(!isOpen)}
      />

      <InlineSidebar
        overlay
        isOpen={isOpen}
        onOpenChange={actions.setOpenness}
        ariaLabel={t('ariaLabel')}
      >
        <InlineSidebar.ExpandedPanel isExpanded={isOpen}>
          {!selectedAction ? (
            <ActionsList
              onAddColumn={(id) => actions.toggleColumn(id, true)}
              actions={allowedActions}
              extraStateStore={extraStateStore}
              onSelect={actions.selectAction}
              availableIds={availableIds}
            />
          ) : (
            <ActionDetail
              key={selectedAction.shortId}
              action={selectedAction}
              actionData={actionsData.find((action) => action.shortId === selectedAction.shortId)}
              focus={focusedVariableId}
              extraStateStore={extraStateStore}
              onBack={actions.unselectAction}
              onColumnToggle={actions.toggleColumn}
            />
          )}
        </InlineSidebar.ExpandedPanel>
        <InlineSidebar.MainPanel>
          <InlineSidebar.Header>
            <Header
              icon={<IconVisibilityShown color="elementBlue100" />}
              title={t('showHide')}
              onClose={actions.close}
            />
          </InlineSidebar.Header>

          <InlineSidebar.Content>
            <ColumnManager
              availableColumns={columnsWithCustomNamesAndClickHandlers}
              columnOrder={columnOrder}
              columnVisibility={columnVisibility}
              onOrderUpdate={setColumnOrder}
              onVisibilityUpdate={setColumnVisibility}
            />
          </InlineSidebar.Content>
        </InlineSidebar.MainPanel>
      </InlineSidebar>
    </>
  );
};

const Header = ({
  title,
  icon,
  onBack,
  onClose,
}: {
  title: string;
  icon: ReactNode;
  onBack?: () => void;
  onClose?: () => void;
}) => {
  const { t } = useScopedTranslate(locale);

  return (
    <Horizontal space={2} align="center" justify="space-between">
      <Horizontal space={2}>
        {icon}
        <Text font="bold18" text={title} />
      </Horizontal>
      <Horizontal space={1} align="center">
        {onBack && (
          <Button
            iconLeft={<IconArrowLeft />}
            text={t('core.back')}
            variant="ghost"
            onClick={onBack}
          />
        )}
        {onClose && (
          <IconButton
            icon={<IconCloseRegular />}
            variant="ghost-purple"
            onClick={onClose}
            dataTestid="close-sidebar"
          />
        )}
      </Horizontal>
    </Horizontal>
  );
};

const ActionCard = ({
  action,
  description,
  onSelect,
  actionUrl,
  badge,
}: {
  action: ActionsDataAction;
  description: string;
  onSelect?: () => void;
  badge?: number;
  actionUrl?: string;
}) => {
  const { t } = useScopedTranslate(locale);

  const title = action.name ?? actionLabels[action.action];
  const icon = action.avatar ? (
    <DesignAvatar src={action.avatar} />
  ) : action.logo ? (
    <SquareImage src={action.logo} />
  ) : (
    <ActionIcon action={action.action} />
  );

  return (
    <Card
      spaceY={2}
      spaceX={4}
      {...(Boolean(onSelect) && {
        onClick: onSelect,
        interactive: true,
        style: { cursor: 'pointer' },
      })}
    >
      <Horizontal space={2} justify="space-between" align="center">
        <TitleDescription
          icon={icon}
          title={title}
          description={description || t('noDescription')}
        />
        <Horizontal space={2} align="center" style={{ marginRight: pxToRem(-8) }}>
          {badge && <Badge>{badge}</Badge>}
          {onSelect && (
            <IconButton variant="ghost-purple" icon={<IconDirectionRight />} onClick={onSelect} />
          )}
          {actionUrl && (
            <IconButton
              size="md"
              variant="ghost-purple"
              icon={<IconOpenNewTab color="elementPurple50" />}
              onClick={() => {
                window.open(actionUrl, '_blank');
              }}
            />
          )}
        </Horizontal>
      </Horizontal>
    </Card>
  );
};

const ActionDetail = ({
  action,
  actionData,
  onBack,
  onColumnToggle,
  focus,
  extraStateStore,
}: {
  action: ActionsDataAction;
  actionData?: ActionsDataAction;
  onBack(): void;
  focus?: string;
  onColumnToggle(id: string, value: boolean): void;
  extraStateStore: ExtraStateStore;
}) => {
  const { t } = useScopedTranslate(locale);
  const listTranslate = useTravellerJourneyListTranslate().t;
  const { useAddedActionIds, useCustomNames } = extraStateStore;
  const addedActionIds = useAddedActionIds();
  const customNames = useCustomNames();
  const { journeyId, workspaceId } = useParams() as { journeyId: string; workspaceId: string };

  if (action.action === 'instantiate_form') {
    // Shouldnt happen really, just satisfying typescript
    return null;
  }

  const statusDefaultLabel = listTranslate('action_status');
  const statusId = makeColumnId(action.shortId, 'status');
  const statusCustomLabel = customNames[statusId];
  const statusLabel = statusCustomLabel
    ? `${statusCustomLabel} (${statusDefaultLabel})`
    : statusDefaultLabel;

  const dateCompletedDefaultLabel = listTranslate('action_date_completed');
  const dateCompletedId = makeColumnId(action.shortId, 'date_completed');
  const dateCompletedCustomLabel = customNames[dateCompletedId];
  const dateCompletedLabel = dateCompletedCustomLabel
    ? `${dateCompletedCustomLabel} (${dateCompletedDefaultLabel})`
    : dateCompletedDefaultLabel;

  const getDescription = nodeTypes[action.action]?.getDescription;
  const description = action.description || getDescription?.(action) || t('noDescription');

  const editActionPath =
    generatePath(routePaths.editJourney, {
      journeyId,
      workspaceId,
      '*': 'journey',
    }) + `?focusedAction=${action.shortId}`;

  return (
    <AnimatePresence>
      <Slide offset={-10} key="header">
        <InlineSidebar.Header>
          <Header
            icon={<IconAttributeJourney color="elementBlue100" />}
            title={t('selectToAdd')}
            onBack={onBack}
          />
        </InlineSidebar.Header>
      </Slide>

      <Slide offset={-10} key="content">
        <InlineSidebar.Content>
          <Stack space={6}>
            <Stack space={4}>
              <Text font="bold14" text={t('actionDetails')} color="textSecondary" caps />
              <ActionCard action={action} description={description} actionUrl={editActionPath} />
              <div>
                <FocusBox isFocused={focus === statusId} style={{ padding: pxToRem(4) }}>
                  <Switch
                    size="sm"
                    iconLeft={<IconStatus size={16} color="purple50" />}
                    label={statusLabel}
                    value={addedActionIds.includes(statusId)}
                    onChange={(value) => onColumnToggle(statusId, value)}
                  />
                </FocusBox>
                <FocusBox isFocused={focus === dateCompletedId} style={{ padding: pxToRem(4) }}>
                  <Switch
                    size="sm"
                    iconLeft={<SmallColumnIcon type="date" />}
                    label={dateCompletedLabel}
                    value={addedActionIds.includes(dateCompletedId)}
                    onChange={(value) => onColumnToggle(dateCompletedId, value)}
                  />
                </FocusBox>
              </div>
            </Stack>

            {actionData && (
              <>
                <InlineSidebar.SectionSeparator />
                <ActionVariables
                  focus={focus}
                  data={actionData}
                  action={action}
                  extraStateStore={extraStateStore}
                  onColumnToggle={onColumnToggle}
                />
              </>
            )}
          </Stack>
        </InlineSidebar.Content>
      </Slide>
    </AnimatePresence>
  );
};

const VariableSwitches = ({
  fields: variables,
  focusedId,
  action,
  onColumnToggle,
  extraStateStore,
  allowToggleAll = false,
}: {
  fields: Array<ActionsDataField>;
  action: ActionsDataAction;
  focusedId?: string;
  onColumnToggle(id: string, value: boolean): void;
  extraStateStore: ExtraStateStore;
  allowToggleAll?: boolean;
}) => {
  const { t } = useScopedTranslate(locale);
  const { useAddedActionIds, useCustomNames } = extraStateStore;
  const addedActionIds = useAddedActionIds();
  const customNames = useCustomNames();

  if (variables.length === 0) {
    return null;
  }

  const allIds = variables.map((variable) => makeVariableColumnId(action.shortId, variable.id));
  const allSelected = allIds.every((id) => addedActionIds.includes(id));

  const toggleAll = (value: boolean) => {
    allIds.forEach((id) => onColumnToggle(id, value));
  };

  return (
    <div>
      {allowToggleAll && allIds.length > 1 && (
        <div style={{ padding: pxToRem(4) }}>
          <Switch
            size="sm"
            label={t('showAllVariables')}
            value={allSelected}
            onChange={toggleAll}
          />
        </div>
      )}
      {variables.map((variable) => {
        const id = makeVariableColumnId(action.shortId, variable.id);
        const label = customNames[id] ? `${customNames[id]} (${variable.label})` : variable.label;
        return (
          <FocusBox isFocused={focusedId === id} key={id} style={{ padding: pxToRem(4) }}>
            <Switch
              size="sm"
              iconLeft={<SmallColumnIcon type={variable.type} />}
              label={label}
              value={addedActionIds.includes(id)}
              onChange={(value) => onColumnToggle(id, value)}
            />
          </FocusBox>
        );
      })}
    </div>
  );
};

const ActionVariables = ({
  action,
  data,
  onColumnToggle,
  focus,
  extraStateStore,
}: {
  action: ActionsDataAction;
  data: ActionsDataAction;
  focus?: string;
  onColumnToggle(id: string, value: boolean): void;
  extraStateStore: ExtraStateStore;
}) => {
  const { t } = useScopedTranslate(locale);

  switch (action.action) {
    case 'combo_send_form': {
      const formResponses = data.variables.formResponses;
      if (!formResponses) {
        return null;
      }

      return (
        <Stack space={4}>
          <Text font="bold14" text={t('formResponses')} color="textSecondary" caps />
          {formResponses.name && (
            <Card spaceY={2} spaceX={4}>
              <TitleDescription
                icon={<IconForm color="elementPurpleInvert" />}
                title={formResponses.name}
                description={formResponses.description || t('noDescription')}
              />
            </Card>
          )}
          <VariableSwitches
            allowToggleAll
            action={action}
            extraStateStore={extraStateStore}
            focusedId={focus}
            onColumnToggle={onColumnToggle}
            fields={formResponses.fields}
          />
        </Stack>
      );
    }
    case 'create_document': {
      const documentFields = data.variables.documentFields;
      const actionFields = data.variables.actionOutput?.fields ?? [];
      if (!documentFields) {
        return null;
      }
      return (
        <Stack space={6}>
          <Stack space={4}>
            <Text font="bold14" text={t('documentFields')} color="textSecondary" caps />
            {documentFields?.name && (
              <Card spaceY={2} spaceX={4}>
                <TitleDescription
                  icon={<IconDocument color="elementPurpleInvert" />}
                  title={documentFields.name}
                  description={documentFields.description || t('noDescription')}
                />
              </Card>
            )}
            <VariableSwitches
              allowToggleAll
              action={action}
              focusedId={focus}
              onColumnToggle={onColumnToggle}
              fields={documentFields.fields}
              extraStateStore={extraStateStore}
            />
          </Stack>

          {actionFields.length > 0 && (
            <>
              <InlineSidebar.SectionSeparator />
              <Stack space={4}>
                <Text font="bold14" text={t('actionOutput')} color="textSecondary" caps />
                <VariableSwitches
                  action={action}
                  focusedId={focus}
                  onColumnToggle={onColumnToggle}
                  fields={actionFields}
                  extraStateStore={extraStateStore}
                />
              </Stack>
            </>
          )}
        </Stack>
      );
    }
    case 'send_sms':
    case 'send_email':
    case 'ask_ai':
    case 'wait_for_time':
    case 'wait_until_fixed_date':
    case 'wait_until_relative_date':
    case 'convert_document':
    case 'integration':
    case 'condition':
    case 'condition_v2':
    default: {
      const actionFields = data.variables.actionOutput?.fields ?? [];
      const configFields = data.variables.configuration?.fields ?? [];

      if (actionFields.length === 0 && configFields.length === 0) {
        return null;
      }

      return (
        <Stack space={4}>
          <Text
            font="bold14"
            text={actionFields.length > 0 ? t('actionOutput') : t('configuration')}
            color="textSecondary"
            caps
          />
          <VariableSwitches
            allowToggleAll={['integration', 'ask_ai_agent'].includes(action.action)}
            action={action}
            focusedId={focus}
            onColumnToggle={onColumnToggle}
            fields={actionFields.length > 0 ? actionFields : configFields}
            extraStateStore={extraStateStore}
          />
        </Stack>
      );
    }
  }
};

const usePossibleResults = (
  actions: Array<ActionsDataAction>,
  customNames: Record<string, string>
) => {
  const { t } = useTravellerJourneyListTranslate();
  return useMemo(() => {
    const results: Array<PreparedSearchResult> = [];

    actions.forEach((action) => {
      (['status', 'date_completed'] as const).forEach((fixedProperty) => {
        const id = makeColumnId(action.shortId, fixedProperty);
        const customName = customNames[id];
        const defaultName = t(`action_${fixedProperty}`);
        const label = customName ? `${customName} (${defaultName})` : defaultName;

        results.push({
          action: action.action,
          id,
          label,
          search: label.toLocaleLowerCase(),
          columnType: fixedProperty === 'status' ? 'single_select' : 'date',
          icon:
            fixedProperty === 'status' ? (
              <IconStatus size={16} color="elementPurple50" />
            ) : undefined,
        });
      });

      Object.entries(action.variables).forEach(([_, variable]) => {
        variable.fields.forEach((field) => {
          const id = makeVariableColumnId(action.shortId, field.id);
          const customName = customNames[id];
          const label = customName ? `${customName} (${field.label})` : field.label;

          const search: Array<string> = [
            label,
            variable.name ?? '',
            variable.description ?? '',
            customName,
            actionLabels[action.action],
          ];
          const searchStr = search.join(' ').toLowerCase();

          results.push({
            action: action.action,
            id,
            label,
            columnType: field.type,
            search: searchStr,
            formName: action.action === 'combo_send_form' ? variable.name : undefined,
            documentName: action.action === 'create_document' ? variable.name : undefined,
          });
        });
      });
    });

    return results;
  }, [actions, customNames, t]);
};

const JourneyActionsList = ({
  actions,
  onSelect,
  availableIds,
}: {
  actions: Array<ActionsDataAction>;
  onSelect: (id: string) => void;
  availableIds: Array<string>;
}) => {
  const { t } = useScopedTranslate(locale);
  return (
    <Fragment>
      <Text font="semibold14" text={t('actionsInJourney')} />
      <Stack space={2}>
        {actions.map((action) => {
          if (action.action === 'instantiate_form') return null;
          const countAlreadyAdded = availableIds.filter((id) =>
            id.startsWith(makeColumnId(action.shortId))
          ).length;
          const getDescription = nodeTypes[action.action]?.getDescription;
          return (
            <ActionCard
              key={action.shortId}
              action={action}
              description={action.description || getDescription?.(action)}
              badge={countAlreadyAdded > 0 ? countAlreadyAdded : undefined}
              onSelect={() => onSelect(action.shortId)}
            />
          );
        })}
      </Stack>
    </Fragment>
  );
};

const SearchResultItem = ({
  result,
  isAdded,
  onAdd,
}: {
  result: PreparedSearchResult;
  isAdded: boolean;
  onAdd: () => void;
}) => {
  const { t } = useScopedTranslate(locale);

  return (
    <Card variant="transparent" spaceX={4} spaceY={3}>
      <Horizontal space={4} justify="space-between" align="center">
        <Stack space={1}>
          <Horizontal space={1}>
            <ActionIcon action={result.action} size={16} />
            <ActionLabel action={result.action} />
          </Horizontal>
          {result.formName && (
            <Horizontal space={1}>
              <IconSubIndicator size={16} color="elementPurple50" />
              <IconForm color="elementPurple50" size={16} />
              <Text font="regular12" text={result.formName} />
            </Horizontal>
          )}
          {result.documentName && (
            <Horizontal space={1}>
              <IconSubIndicator size={16} color="elementPurple50" />
              <IconDocument color="elementPurple50" size={16} />
              <Text font="regular12" text={result.documentName} />
            </Horizontal>
          )}
          <Horizontal space={1}>
            <IconSubIndicator size={16} color="elementPurple50" />
            {result.icon ?? <SmallColumnIcon type={result.columnType} />}
            <Text font="regular12" text={result.label} />
          </Horizontal>
        </Stack>
        <AddContainer>
          {isAdded ? (
            <Text font="semibold14" color="textSecondary" text={t('addedColumn')} />
          ) : (
            <Button
              iconLeft={<IconReturn />}
              size="md"
              variant="primary-blue"
              text={t('addColumn')}
              onClick={onAdd}
            />
          )}
        </AddContainer>
      </Horizontal>
    </Card>
  );
};

type PreparedSearchResult = {
  action: ACTION_TYPE;
  id: string;
  label: string;
  search: string;
  columnType: ColumnType;
  icon?: JSX.Element;
  formName?: string;
  documentName?: string;
};

const FilteredSearchResults = ({
  results,
  addedActionIds,
  onAddColumn,
}: {
  results: Array<PreparedSearchResult>;
  addedActionIds: Array<string>;
  onAddColumn: (id: string) => void;
}) => {
  if (results.length === 0) {
    return <EmptySearchState variant="small" skipMargin />;
  }

  return (
    <Stack space={2}>
      {results.map((result) => (
        <SearchResultItem
          key={result.id}
          result={result}
          isAdded={addedActionIds.includes(result.id)}
          onAdd={() => onAddColumn(result.id)}
        />
      ))}
    </Stack>
  );
};

const ActionsList = ({
  actions,
  onSelect,
  onAddColumn,
  extraStateStore,
  availableIds,
}: {
  actions: Array<ActionsDataAction>;
  onSelect(id: string): void;
  onAddColumn(id: string): void;
  extraStateStore: ExtraStateStore;
  availableIds: Array<string>;
}) => {
  const { t } = useScopedTranslate(locale);
  const [search, setSearch] = useState('');
  const { useAddedActionIds, useCustomNames } = extraStateStore;
  const addedActionIds = useAddedActionIds();
  const customNames = useCustomNames();

  const possibleResults = usePossibleResults(actions, customNames);

  const filteredResults = useMemo(() => {
    if (!search) return [];

    const loweredSearch = search.toLowerCase();
    return possibleResults.filter((result) => result.search.includes(loweredSearch));
  }, [possibleResults, search]);

  return (
    <AnimatePresence>
      <Slide offset={10} key="header">
        <InlineSidebar.Header>
          <Stack space={2} orientation="horizontal" align="center">
            <IconAddPlus color="elementBlue100" />
            <Text font="bold18" text={t('addFromJourneyAction')} />
          </Stack>
        </InlineSidebar.Header>
      </Slide>

      <Slide offset={10} key="content">
        <InlineSidebar.Content>
          <Stack space={4}>
            <SearchBox placeholder={t('core.search')} defaultValue={search} onChange={setSearch} />
            {search.length > 0 ? (
              <FilteredSearchResults
                results={filteredResults}
                addedActionIds={addedActionIds}
                onAddColumn={onAddColumn}
              />
            ) : (
              <JourneyActionsList
                availableIds={availableIds}
                actions={actions}
                onSelect={onSelect}
              />
            )}
          </Stack>
        </InlineSidebar.Content>
      </Slide>
    </AnimatePresence>
  );
};

const locale = {
  en: {
    columns: 'Show/Hide Fields',
    noDescription: 'No description',
    addFromJourneyAction: 'Add field from Journey action',
    addColumn: 'Add',
    addedColumn: 'Added',
    actionsInJourney: 'Actions in journey',
    actionOutput: 'Action output',
    configuration: 'Configuration',
    documentFields: 'Document fields',
    formResponses: 'Form responses - dynamic',
    actionDetails: 'Action details',
    selectToAdd: 'Select field/s to add',
    addActionField: 'Add action field',
    closeFields: 'Close fields',
    showHide: 'Show / Hide fields',
    showAllVariables: 'Show/Hide all',
    ariaLabel: 'Manage columns',
  },
};

const AddContainer = styled.div({
  height: pxToRem(40),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});
